// ---------------------------------------------------------------------------
// clientDupes — duplicate-client detection (2026-07-02, tester feedback).
// Rules agreed with Jaylen:
//   STRONG (blocking "looks like X" dialog):
//     • any phone number matches any of an existing client's numbers, OR
//     • exact normalized name match AND same street (number + first word)
//   SOFT (non-blocking heads-up):
//     • exact normalized name match only
// Checked on CREATE in both the desktop intake and the estimator intake.
// ---------------------------------------------------------------------------

import { phoneDigits, phonesOf } from './phone';

function normName(s) {
    return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// "1849 Jackson Grove Rd, Columbus, NC" → "1849 jackson" — street number
// plus first street word is specific enough to pair with a name match
// without being defeated by suite numbers / city formatting.
function streetKey(s) {
    const m = String(s || '').toLowerCase().match(/(\d+)\s+([a-z]+)/);
    return m ? `${m[1]} ${m[2]}` : '';
}

function clientPhoneKeys(client) {
    const keys = new Set();
    for (const p of phonesOf(client)) {
        const d = phoneDigits(p.number);
        if (d.length >= 7) keys.add(d);
    }
    const legacy = phoneDigits(client?.phone);
    if (legacy.length >= 7) keys.add(legacy);
    const secondary = phoneDigits(client?.secondary_phone);
    if (secondary.length >= 7) keys.add(secondary);
    return keys;
}

// Returns { strength: 'strong'|'soft', match, matches, reason } or null.
//   • match   — the primary candidate (first found), kept for callers that
//               only surface one.
//   • matches — ALL candidates behind this result. For a shared phone number
//               this can be several clients (spouse / landline / stale test
//               rows), so the dialog can offer a pick list instead of guessing
//               one (BUG-4).
export function findLikelyDuplicates(candidate, existingClients) {
    const candPhones = clientPhoneKeys(candidate);
    const candName = normName(candidate.name);
    const candStreet = streetKey(candidate.origin_address) || streetKey(candidate.destination_address);

    const phoneMatches = [];
    let strongNameStreet = null;
    let soft = null;
    for (const c of existingClients || []) {
        if (!c || c.id === candidate.id) continue;

        // Phone match — strongest possible signal. Collect ALL of them.
        const theirPhones = clientPhoneKeys(c);
        let phoneHit = false;
        for (const k of candPhones) { if (theirPhones.has(k)) { phoneHit = true; break; } }
        if (phoneHit) { phoneMatches.push(c); continue; }

        // Name (+ street) matching.
        if (candName && normName(c.name) === candName) {
            const theirStreet = streetKey(c.origin_address) || streetKey(c.destination_address);
            if (candStreet && theirStreet && candStreet === theirStreet) {
                if (!strongNameStreet) strongNameStreet = c;
            } else if (!soft) {
                soft = c;
            }
        }
    }

    if (phoneMatches.length) {
        return {
            strength: 'strong',
            match: phoneMatches[0],
            matches: phoneMatches,
            reason: phoneMatches.length > 1
                ? `${phoneMatches.length} existing clients share this phone number`
                : 'same phone number',
        };
    }
    if (strongNameStreet) {
        return { strength: 'strong', match: strongNameStreet, matches: [strongNameStreet], reason: 'same name and street address' };
    }
    if (soft) {
        return { strength: 'soft', match: soft, matches: [soft], reason: 'same name' };
    }
    return null;
}
